import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { AuthContext } from "../../context/AuthContext";
import ProgramaMensualImagen from "./ProgramaMensualImagen";
import {
  FaArrowLeft, FaCalendarCheck, FaSyncAlt, FaDownload,
} from "react-icons/fa";

const API = "/api";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const formatoFecha = (fecha) =>
  new Date(fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "2-digit", timeZone: "UTC" });

const formatoHora = (hora) => (hora ? hora.slice(0, 5) : "");

// El "mes en curso" se calcula con la hora de México (donde está la
// iglesia), no con la hora local del dispositivo de quien lo abre: así el
// corte de mes coincide siempre con el del backend, sin importar la zona
// horaria del navegador (p. ej. un administrador viajando).
const añoMesMexico = () => {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "numeric",
  }).formatToParts(new Date());
  return {
    anio: Number(partes.find((p) => p.type === "year").value),
    mes: Number(partes.find((p) => p.type === "month").value),
  };
};

export default function ProgramaMensualAdmin() {
  const { token } = useContext(AuthContext);
  const { anio: anioActual, mes: mesActual } = añoMesMexico();

  const [anio, setAnio] = useState(anioActual);
  const [mes, setMes] = useState(mesActual);

  // Un mes ya terminado (anterior al mes en curso) queda bloqueado para edición.
  const mesTerminado = anio < anioActual || (anio === anioActual && mes < mesActual);
  const [filas, setFilas] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [directores, setDirectores] = useState([]);
  const [proyeccion, setProyeccion] = useState([]);
  const [listaAseo, setListaAseo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const hojaRef = useRef(null);

  const [notificacion, setNotificacion] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const mostrarToast = (mensaje, tipo = "success") => {
    setNotificacion({ mostrar: true, mensaje, tipo });
    setTimeout(() => setNotificacion({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargarCatalogos = useCallback(async () => {
    try {
      const [resMiembros, resDirectores, resProyeccion, resAseo] = await Promise.all([
        fetch(`${API}/miembros?estatus=activos`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/catalogos/directores_musica?limit=1000&estatus=activos`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/catalogos/proyeccion?limit=1000&estatus=activos`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/catalogos/lista_aseo?limit=1000&estatus=activos`, { credentials: "include", headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setMiembros(await resMiembros.json());
      const directoresData = await resDirectores.json();
      setDirectores(directoresData.data || []);
      const proyeccionData = await resProyeccion.json();
      setProyeccion(proyeccionData.data || []);
      const aseoData = await resAseo.json();
      setListaAseo(aseoData.data || []);
    } catch (error) {
      console.error("Error cargando catálogos:", error);
    }
  }, [token]);

  const cargarPrograma = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/programa-mensual?anio=${anio}&mes=${mes}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setFilas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando programa mensual:", error);
    } finally {
      setLoading(false);
    }
  }, [anio, mes, token]);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);
  useEffect(() => { cargarPrograma(); }, [cargarPrograma]);

  const nombreMiembro = useMemo(() => {
    const mapa = {};
    miembros.forEach((m) => { mapa[m.id_miembro] = [m.nombre, m.paterno, m.materno].filter(Boolean).join(" "); });
    return mapa;
  }, [miembros]);

  // Agrupa las filas por semana (jueves-miércoles) para mostrar Aseo,
  // Cumpleaños y Aniversario en una sola celda combinada por semana.
  const { primeraFilaDeSemana, filasPorSemana } = useMemo(() => {
    const primera = {};
    const conteo = {};
    filas.forEach((f) => {
      if (primera[f.semana_index] === undefined) primera[f.semana_index] = f.id_programa;
      conteo[f.semana_index] = (conteo[f.semana_index] || 0) + 1;
    });
    return { primeraFilaDeSemana: primera, filasPorSemana: conteo };
  }, [filas]);

  const generarPrograma = async () => {
    setGenerando(true);
    try {
      const res = await fetch(`${API}/programa-mensual/generar`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ anio, mes }),
      });
      const data = await res.json();
      if (!res.ok) {
        mostrarToast(data.error || "Error generando el programa", "error");
        return;
      }
      mostrarToast("Programa generado correctamente");
      cargarPrograma();
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    } finally {
      setGenerando(false);
    }
  };

  const actualizarCampo = async (idPrograma, campo, valor) => {
    setFilas((prev) => prev.map((f) => (f.id_programa === idPrograma ? { ...f, [campo]: valor } : f)));
    try {
      const res = await fetch(`${API}/programa-mensual/${idPrograma}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [campo]: valor }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        mostrarToast(data.error || "Error guardando el cambio", "error");
        if (res.status === 403) cargarPrograma();
      }
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    }
  };

  // El aseo se asigna por semana (no por servicio): al cambiarlo se
  // actualizan todas las filas de esa semana a la vez.
  const actualizarAseoSemana = async (semanaIndex, idAseo) => {
    setFilas((prev) => prev.map((f) => (f.semana_index === semanaIndex ? { ...f, id_aseo: idAseo } : f)));
    try {
      const res = await fetch(`${API}/programa-mensual/semana-aseo`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ anio, mes, semana_index: semanaIndex, id_aseo: idAseo }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        mostrarToast(data.error || "Error guardando el cambio", "error");
        if (res.status === 403) cargarPrograma();
      }
    } catch (error) {
      mostrarToast("Error inesperado", "error");
    }
  };

  const descargarImagen = async () => {
    if (!hojaRef.current) return;
    setDescargando(true);
    try {
      const url = await toPng(hojaRef.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `programa_mensual_${MESES[mes - 1].toLowerCase()}_${anio}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error generando la imagen del programa:", error);
      mostrarToast("No se pudo generar la imagen", "error");
    } finally {
      setDescargando(false);
    }
  };

  const selectClass = "w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 bg-white";

  return (
    <div className="p-6 bg-slate-50">

      {notificacion.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${notificacion.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {notificacion.mensaje}
        </div>
      )}

      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600 flex items-center gap-1 inline-flex">
          <FaArrowLeft className="text-xs" /> Administración
        </Link>
      </div>

      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Programa Mensual</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Director, Maestro de Escuela Dominical, Predicador, Proyección, Aseo, cumpleaños y aniversarios por servicio.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={descargarImagen}
            disabled={filas.length === 0 || loading || descargando}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
          >
            <FaDownload className="text-xs" /> {descargando ? "Generando imagen..." : "Descargar"}
          </button>
          <button
            onClick={generarPrograma}
            disabled={generando || mesTerminado}
            title={mesTerminado ? "Este mes ya terminó y no puede modificarse" : undefined}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-60"
          >
            <FaSyncAlt className={`text-xs ${generando ? "animate-spin" : ""}`} />
            {generando ? "Generando..." : "Generar programa"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Mes</label>
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400">
            {MESES.map((nombreMes, i) => (
              <option key={i} value={i + 1}>{nombreMes}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Año</label>
          <input
            type="number"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
            className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>
        {mesTerminado && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Este mes ya terminó: el programa se muestra en modo de solo lectura.
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-3 text-left font-semibold">Fecha</th>
                <th className="px-3 py-3 text-left font-semibold">Culto</th>
                <th className="px-3 py-3 text-left font-semibold min-w-[160px]">Director</th>
                <th className="px-3 py-3 text-left font-semibold min-w-[160px]">Maestro E.D.</th>
                <th className="px-3 py-3 text-left font-semibold min-w-[160px]">Predicador</th>
                <th className="px-3 py-3 text-left font-semibold min-w-[160px]">Proyección</th>
                <th className="px-3 py-3 text-left font-semibold min-w-[180px]">Aseo</th>
                <th className="px-3 py-3 text-left font-semibold min-w-[160px]">Cumpleaños</th>
                <th className="px-3 py-3 text-left font-semibold min-w-[160px]">Aniversario</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400">Cargando...</td></tr>
              ) : filas.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-slate-400">
                  No hay programa generado para {MESES[mes - 1]} {anio}. Verifica que existan Servicios registrados para ese mes y usa "Generar programa".
                </td></tr>
              ) : (
                filas.map((f) => (
                  <tr key={f.id_programa} className="border-t border-slate-100 hover:bg-slate-50 transition align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{formatoFecha(f.fecha)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                      {f.culto}
                      <div className="text-[10px] text-slate-400">{formatoHora(f.hora)}</div>
                    </td>
                    <td className="px-3 py-2">
                      {mesTerminado ? (
                        <span className="text-slate-600">{nombreMiembro[f.id_miembro_director] || <span className="text-slate-300">—</span>}</span>
                      ) : (
                        <select
                          className={selectClass}
                          value={f.id_miembro_director || ""}
                          onChange={(e) => actualizarCampo(f.id_programa, "id_miembro_director", e.target.value || null)}
                        >
                          <option value="">-- Selecciona --</option>
                          {directores.map((d) => (
                            <option key={d.id_miembro} value={d.id_miembro}>
                              {[d.nombre, d.paterno, d.materno].filter(Boolean).join(" ")}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {!f.es_escuela_dominical ? (
                        <span className="text-slate-300">—</span>
                      ) : mesTerminado ? (
                        <span className="text-slate-600">{nombreMiembro[f.id_miembro_maestro_ed] || <span className="text-slate-300">—</span>}</span>
                      ) : (
                        <select
                          className={selectClass}
                          value={f.id_miembro_maestro_ed || ""}
                          onChange={(e) => actualizarCampo(f.id_programa, "id_miembro_maestro_ed", e.target.value || null)}
                        >
                          <option value="">-- Selecciona --</option>
                          {miembros.map((m) => (
                            <option key={m.id_miembro} value={m.id_miembro}>
                              {[m.nombre, m.paterno, m.materno].filter(Boolean).join(" ")}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {mesTerminado ? (
                        <span className="text-slate-600">{f.predicador || <span className="text-slate-300">—</span>}</span>
                      ) : (
                        <input
                          type="text"
                          list="lista-predicadores"
                          defaultValue={f.predicador || ""}
                          onBlur={(e) => {
                            if (e.target.value !== (f.predicador || "")) {
                              actualizarCampo(f.id_programa, "predicador", e.target.value);
                            }
                          }}
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                          placeholder="Nombre del predicador"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {mesTerminado ? (
                        <span className="text-slate-600">{nombreMiembro[f.id_miembro_proyeccion] || <span className="text-slate-300">—</span>}</span>
                      ) : (
                        <select
                          className={selectClass}
                          value={f.id_miembro_proyeccion || ""}
                          onChange={(e) => actualizarCampo(f.id_programa, "id_miembro_proyeccion", e.target.value || null)}
                        >
                          <option value="">-- Selecciona --</option>
                          {proyeccion.map((p) => (
                            <option key={p.id_miembro} value={p.id_miembro}>
                              {[p.nombre, p.paterno, p.materno].filter(Boolean).join(" ")}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    {primeraFilaDeSemana[f.semana_index] === f.id_programa && (
                      <>
                        <td className="px-3 py-2 align-middle bg-slate-50/50" rowSpan={filasPorSemana[f.semana_index]}>
                          {mesTerminado ? (
                            <span className="text-slate-600">
                              {f.aseo_familia ? `${f.aseo_familia}${f.aseo_hermano ? ` - ${f.aseo_hermano}` : ""}` : <span className="text-slate-300">—</span>}
                            </span>
                          ) : (
                            <select
                              className={selectClass}
                              value={f.id_aseo || ""}
                              onChange={(e) => actualizarAseoSemana(f.semana_index, e.target.value || null)}
                            >
                              <option value="">-- Selecciona --</option>
                              {listaAseo.map((a) => (
                                <option key={a.id_aseo} value={a.id_aseo}>
                                  {a.familia}{a.hermano ? ` - ${a.hermano}` : ""}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-3 py-2 align-middle text-slate-600 bg-slate-50/50" rowSpan={filasPorSemana[f.semana_index]}>
                          {(f.cumpleanios || []).length > 0 ? f.cumpleanios.join(", ") : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 align-middle text-slate-600 bg-slate-50/50" rowSpan={filasPorSemana[f.semana_index]}>
                          {(f.aniversarios || []).length > 0 ? f.aniversarios.join(", ") : <span className="text-slate-300">—</span>}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <datalist id="lista-predicadores">
        {miembros.map((m) => (
          <option key={m.id_miembro} value={[m.nombre, m.paterno, m.materno].filter(Boolean).join(" ")} />
        ))}
      </datalist>

      {filas.length > 0 && (
        <ProgramaMensualImagen
          ref={hojaRef}
          filas={filas}
          nombreMes={MESES[mes - 1]}
          anio={anio}
          nombreMiembro={nombreMiembro}
        />
      )}
    </div>
  );
}
